import { Package, Receipt, User, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TicketContext } from "../types";

type T = Record<string, string>;
type Entity = NonNullable<TicketContext["entity"]>;

const META: Record<Entity["type"], { icon: typeof Package; labelKey: string }> = {
  product: { icon: Package, labelKey: "entityProduct" },
  order: { icon: Receipt, labelKey: "entityOrder" },
  customer: { icon: User, labelKey: "entityCustomer" },
  page: { icon: FileText, labelKey: "entityPage" },
};

/**
 * Rich preview of the record a ticket was raised from — a thumbnail (real
 * product photo when the backend provides `imageUrl`, else an icon tile) plus
 * the entity type, name, and id/url.
 */
export function EntityPreview({
  entity,
  url,
  t,
  size = "md",
}: {
  entity: Entity;
  url: string;
  t: T;
  size?: "sm" | "md";
}) {
  const { icon: Icon, labelKey } = META[entity.type];
  const thumb = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const primary = entity.name || entity.id || url;
  const secondary = entity.id || url;

  return (
    <div className="flex items-center gap-2.5">
      {entity.imageUrl ? (
        <img
          src={entity.imageUrl}
          alt=""
          className={cn("shrink-0 rounded object-cover", thumb)}
        />
      ) : (
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded bg-blue-50 text-primary dark:bg-blue-500/10",
            thumb,
          )}
        >
          <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{t[labelKey]}</p>
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{primary}</p>
        {secondary !== primary && (
          <p className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">
            {secondary}
          </p>
        )}
      </div>
    </div>
  );
}
